import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  encodeJpegWithoutMetadata,
  loadImageUrl,
  outputFilename,
  outputMime,
  ObjectUrlTracker,
  processMetadataInWorker,
  type ImageMetadataFormat,
  type MetadataSummary,
} from '@/features/image-metadata-cleaner'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type RemoveExifStatus = 'ready' | 'processing' | 'done' | 'error'
type RemoveExifErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'too_large_dimensions'
  | 'corrupt_image'
  | 'canvas_error'
  | 'cleanup_failed'

type RemoveExifCopy = Translations['removeExif']

interface RemoveExifEntry {
  file: File
  format: ImageMetadataFormat
  metadata: MetadataSummary
  originalUrl: string
  cleanedBlob: Blob | null
  cleanedUrl: string | null
  originalSize: number
  cleanedSize: number | null
  width: number
  height: number
  status: RemoveExifStatus
  error?: string
}

const UTILITY_KEY = 'remove-exif'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp']

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function formatLabel(format: ImageMetadataFormat): string {
  if (format === 'jpeg') return 'JPG'
  return format.toUpperCase()
}

function hasSupportedNameOrType(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    ACCEPTED_TYPES.includes(type) ||
    ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  )
}

function validateFile(
  file: File,
  copy: RemoveExifCopy,
): { message: string; code: RemoveExifErrorCode } | null {
  if (!hasSupportedNameOrType(file)) {
    return { message: copy.errorsText.unsupported, code: 'unsupported_type' }
  }
  if (file.size > MAX_FILE_SIZE) {
    return { message: copy.errorsText.tooLarge, code: 'too_large' }
  }
  return null
}

class RemoveExifError extends Error {
  constructor(
    message: string,
    readonly code: RemoveExifErrorCode,
  ) {
    super(message)
  }
}

function metadataError(error: unknown, copy: RemoveExifCopy): RemoveExifError {
  if (error instanceof Error) {
    if (error.message === 'too_large_dimensions') {
      return new RemoveExifError(copy.errorsText.tooLargeDimensions, 'too_large_dimensions')
    }
    if (error.message === 'unsupported_type') {
      return new RemoveExifError(copy.errorsText.unsupported, 'unsupported_type')
    }
    return new RemoveExifError(
      copy.errorsText.corrupt,
      'corrupt_image',
    )
  }
  return new RemoveExifError(copy.errorsText.corrupt, 'corrupt_image')
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

function revokeEntry(entry: RemoveExifEntry | null): void {
  if (!entry) return
  URL.revokeObjectURL(entry.originalUrl)
  if (entry.cleanedUrl) URL.revokeObjectURL(entry.cleanedUrl)
}

function removableMetadataFound(summary: MetadataSummary): boolean {
  return summary.exif || summary.xmp || summary.text || summary.time
}

export function RemoveExifPage() {
  const t = useT()
  const copy = t.removeExif
  const [entry, setEntry] = useState<RemoveExifEntry | null>(null)
  const [dropActive, setDropActive] = useState(false)
  const [isPreparing, setIsPreparing] = useState(false)
  const [pageError, setPageError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const entryRef = useRef<RemoveExifEntry | null>(null)
  const operationRef = useRef(0)
  const mountedRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)
  const preparingUrlTrackerRef = useRef(new ObjectUrlTracker())

  const replaceEntry = useCallback((next: RemoveExifEntry | null) => {
    entryRef.current = next
    setEntry(next)
  }, [])

  useEffect(
    () => {
      // React StrictMode replays effects in development. Re-establish the guard
      // during every setup so the replay cleanup cannot suppress the next task.
      mountedRef.current = true
      return () => {
        mountedRef.current = false
        operationRef.current += 1
        abortRef.current?.abort()
        preparingUrlTrackerRef.current.release()
        revokeEntry(entryRef.current)
      }
    },
    [],
  )

  const prepareFile = useCallback(
    async (file: File) => {
      const operation = ++operationRef.current
      abortRef.current?.abort()
      preparingUrlTrackerRef.current.release()
      const controller = new AbortController()
      abortRef.current = controller
      const validationError = validateFile(file, copy)
      if (validationError) {
        revokeEntry(entryRef.current)
        replaceEntry(null)
        setPageError(validationError.message)
        toolError(UTILITY_KEY, validationError.code)
        return
      }

      setIsPreparing(true)
      setPageError(null)
      const originalUrl = URL.createObjectURL(file)
      preparingUrlTrackerRef.current.track(originalUrl)
      try {
        const bytes = new Uint8Array(await file.arrayBuffer())
        const inspected = await processMetadataInWorker(bytes, 'inspect', controller.signal)
        await loadImageUrl(originalUrl, controller.signal)
        if (!mountedRef.current || operation !== operationRef.current) {
          URL.revokeObjectURL(originalUrl)
          return
        }
        revokeEntry(entryRef.current)
        preparingUrlTrackerRef.current.take()
        replaceEntry({
          file,
          format: inspected.format,
          metadata: inspected.summary,
          originalUrl,
          cleanedBlob: null,
          cleanedUrl: null,
          originalSize: file.size,
          cleanedSize: null,
          width: inspected.width,
          height: inspected.height,
          status: 'ready',
        })
      } catch (error) {
        preparingUrlTrackerRef.current.release(originalUrl)
        if (!mountedRef.current || operation !== operationRef.current) return
        revokeEntry(entryRef.current)
        const normalized =
          error instanceof RemoveExifError
            ? error
            : metadataError(error, copy)
        replaceEntry(null)
        setPageError(normalized.message)
        toolError(UTILITY_KEY, normalized.code)
      } finally {
        if (mountedRef.current && operation === operationRef.current) {
          setIsPreparing(false)
          if (abortRef.current === controller) abortRef.current = null
        }
      }
    },
    [copy, replaceEntry],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const file = Array.from(incoming)[0]
      if (file && !isPreparing && entry?.status !== 'processing') void prepareFile(file)
    },
    [entry?.status, isPreparing, prepareFile],
  )

  const clearFile = useCallback(() => {
    operationRef.current += 1
    abortRef.current?.abort()
    abortRef.current = null
    preparingUrlTrackerRef.current.release()
    revokeEntry(entryRef.current)
    replaceEntry(null)
    setIsPreparing(false)
    setPageError(null)
  }, [replaceEntry])

  const handleClean = useCallback(async () => {
    const current = entryRef.current
    if (!current || current.status === 'processing') return
    const operation = ++operationRef.current
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const metadataStatus = removableMetadataFound(current.metadata) ? 'found' : 'not_found'
    toolInvoke(UTILITY_KEY, { metadata: metadataStatus, format: current.format })
    const startTime = performance.now()
    const processingEntry = { ...current, status: 'processing' as const, error: undefined }
    replaceEntry(processingEntry)

    try {
      let blob: Blob
      if (current.format === 'jpeg') {
        const image = await loadImageUrl(current.originalUrl, controller.signal)
        try {
          blob = await encodeJpegWithoutMetadata(image)
        } catch (error) {
          throw new RemoveExifError(
            error instanceof Error && error.message === 'canvas_context'
              ? copy.errorsText.canvas
              : copy.errorsText.cleanup,
            error instanceof Error && error.message === 'canvas_context'
              ? 'canvas_error'
              : 'cleanup_failed',
          )
        }
      } else {
        const bytes = new Uint8Array(await current.file.arrayBuffer())
        const cleaned = await processMetadataInWorker(bytes, 'clean', controller.signal)
        blob = new Blob([cleaned.bytes], { type: outputMime(current.format) })
      }
      if (!mountedRef.current || operation !== operationRef.current) return
      if (current.cleanedUrl) URL.revokeObjectURL(current.cleanedUrl)
      const cleanedUrl = URL.createObjectURL(blob)
      replaceEntry({
        ...current,
        cleanedBlob: blob,
        cleanedUrl,
        cleanedSize: blob.size,
        status: 'done',
      })
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
        metadata: metadataStatus,
        format: current.format,
      })
    } catch (error) {
      if (!mountedRef.current || operation !== operationRef.current) return
      const normalized =
        error instanceof RemoveExifError
          ? error
          : metadataError(error, copy)
      toolError(UTILITY_KEY, normalized.code, {
        metadata: metadataStatus,
        format: current.format,
      })
      replaceEntry({ ...current, status: 'error', error: normalized.message })
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [copy, replaceEntry])

  const handleDownload = useCallback(() => {
    const current = entryRef.current
    if (!current?.cleanedBlob) return
    downloadBlob(current.cleanedBlob, outputFilename(current.file.name, current.format))
  }, [])

  const metadataRows = entry
    ? [
        { id: 'exif', label: copy.metadataLabels.exif, found: entry.metadata.exif },
        { id: 'xmp', label: copy.metadataLabels.xmp, found: entry.metadata.xmp },
        { id: 'text', label: copy.metadataLabels.text, found: entry.metadata.text },
        { id: 'time', label: copy.metadataLabels.time, found: entry.metadata.time },
      ] as const
    : []

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['JPG, PNG, WebP', t.common.maxFileSize]}
        collapsed={Boolean(entry)}
      />

      <div className={cn('grid grid-cols-1 gap-6', entry && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start')}>
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              entry && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={(event) => {
              event.preventDefault()
              setDropActive(true)
            }}
            onDragLeave={(event) => {
              event.preventDefault()
              setDropActive(false)
            }}
            onDrop={(event) => {
              event.preventDefault()
              setDropActive(false)
              if (event.dataTransfer.files.length > 0) addFiles(event.dataTransfer.files)
            }}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                META
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  entry && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                disabled={isPreparing || entry?.status === 'processing'}
                className="hidden"
                onChange={(event) => {
                  if (event.target.files?.length) addFiles(event.target.files)
                  event.target.value = ''
                }}
              />
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  disabled={isPreparing || entry?.status === 'processing'}
                  onClick={() => inputRef.current?.click()}
                >
                  {entry ? copy.replaceFile : copy.chooseFile}
                </Button>
                {entry && (
                  <Button variant="outline" onClick={clearFile}>
                    {t.common.remove}
                  </Button>
                )}
              </div>
              {isPreparing && <p className="mt-5 text-sm text-muted-foreground">{copy.inspecting}</p>}
              {pageError && (
                <div className="mx-auto mt-5 max-w-md rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {pageError}
                </div>
              )}
            </CardContent>
          </Card>

          {entry && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold tracking-normal">{copy.previewTitle}</h2>
              <Card className="overflow-hidden shadow-sm">
                <CardContent className="p-0">
                  <div className="grid grid-cols-2 gap-px bg-border">
                    {[
                      { label: copy.sourceLabel, url: entry.originalUrl, alt: entry.file.name },
                      {
                        label: copy.resultLabel,
                        url: entry.cleanedUrl,
                        alt: outputFilename(entry.file.name, entry.format),
                      },
                    ].map((preview) => (
                      <div className="bg-muted" key={preview.label}>
                        <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                          <span>{preview.label}</span>
                          <span>{formatLabel(entry.format)}</span>
                        </div>
                        <div className="aspect-video bg-background/65">
                          {preview.url ? (
                            <img src={preview.url} alt={preview.alt} className="h-full w-full object-contain" />
                          ) : (
                            <div className="grid h-full place-items-center text-xs text-muted-foreground">
                              {copy.waitingPreview}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-4 p-4">
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span className="rounded-md border bg-background px-2 py-1">{entry.width} x {entry.height}</span>
                      <span className="rounded-md border bg-background px-2 py-1">{formatSize(entry.originalSize)}</span>
                      {entry.cleanedSize !== null && (
                        <span className="rounded-md border bg-background px-2 py-1">{formatSize(entry.cleanedSize)}</span>
                      )}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {metadataRows.map((row) => (
                        <div className="flex justify-between rounded-md border px-3 py-2 text-sm" key={row.id}>
                          <span>{row.label}</span>
                          <strong>{row.found ? copy.metadataYes : copy.metadataNo}</strong>
                        </div>
                      ))}
                      <div className="flex justify-between rounded-md border px-3 py-2 text-sm">
                        <span>{copy.metadataLabels.colorProfile}</span>
                        <strong>
                          {entry.metadata.colorProfilePreserved
                            ? copy.colorProfilePreserved
                            : copy.metadataNo}
                        </strong>
                      </div>
                    </div>
                    {entry.format === 'jpeg' && (
                      <p className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">
                        {copy.jpegLossyNote}
                      </p>
                    )}
                    {entry.status === 'processing' && <p className="text-sm text-muted-foreground">{copy.processing}</p>}
                    {entry.status === 'done' && <p className="text-sm font-medium text-primary">{copy.doneStatus}</p>}
                    {entry.status === 'error' && <p className="text-sm text-destructive">{entry.error}</p>}
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
        </div>

        {entry && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{copy.summary}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{copy.metadataLabel}</span>
                    <strong className="font-medium text-foreground">
                      {removableMetadataFound(entry.metadata) ? copy.metadataYes : copy.metadataNo}
                    </strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>{copy.originalSize}</span>
                    <strong className="font-medium text-foreground">{formatSize(entry.originalSize)}</strong>
                  </div>
                </div>
                <Button
                  className="w-full"
                  size="lg"
                  onClick={entry.status === 'done' ? handleDownload : handleClean}
                  disabled={entry.status === 'processing'}
                >
                  {entry.status === 'processing'
                    ? copy.processing
                    : entry.status === 'done'
                      ? t.common.download
                      : copy.cleanButton}
                </Button>
              </CardContent>
            </Card>
            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p>
          </div>
        ))}
      </section>
    </div>
  )
}
